// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DialogHeader.tsx
 * @input Uses React, useEffect, useRef, LayoutHeader, Button, Icon, Heading, Text, DialogContext
 * @output Exports DialogHeader component and DialogHeaderProps
 * @position Dialog header component; used with Dialog and Layout
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Dialog/Dialog.doc.mjs
 * - /packages/core/src/Dialog/DialogHeader.test.tsx
 * - /packages/core/src/Dialog/index.ts
 * - /apps/storybook/stories/Dialog.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Dialog/ (showcase blocks)
 */
import { useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars, sizeVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { LayoutHeader } from "../Layout/LayoutHeader.js";
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import { Heading } from "../Heading/Heading.js";
import { Text } from "../Text/Text.js";
import { useDialogContext } from "./DialogContext.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  titleFocusable: {
    kI3sdo: "x1a2a7pz",
    $$css: true
  }
};
/**
 * Header component designed specifically for Dialog.
 *
 * Renders a title that receives focus when a modal dialog opens (for screen reader accessibility)
 * and an optional close button. Inline documentation previews suppress this autofocus.
 * The title is an h2 element with tabIndex={-1} so it can be programmatically focused but
 * doesn't appear in the tab order. The title also names the parent Dialog via
 * aria-labelledby (unless the Dialog receives an explicit aria-label/aria-labelledby).
 *
 * Uses LayoutHeader internally for consistent styling with other layout headers.
 *
 * @example
 * ```
 * <Dialog isOpen={isOpen} onOpenChange={open => setIsOpen(open)}>
 *   <Layout
 *     header={<DialogHeader title="Modal Title" onOpenChange={open => setIsOpen(open)} />}
 *     content={<LayoutContent>Content</LayoutContent>}
 *     footer={<LayoutFooter hasDivider>Actions</LayoutFooter>}
 *   />
 * </Dialog>
 * ```
 */
export function DialogHeader({
  title,
  subtitle,
  onOpenChange,
  startContent,
  endContent,
  hasDivider,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const titleRef = useRef(null);
  const dialogContext = useDialogContext();
  const shouldAutoFocus = dialogContext?.isInline !== true;
  const titleId = dialogContext?.titleId;

  // Auto-focus the title when mounted for screen reader accessibility.
  // Inline dialogs are documentation/showcase previews, so suppress focus to
  // avoid stealing scroll position from the surrounding page.
  // The parent Dialog detects this title (by `titleId`) via a callback ref to
  // set its default aria-labelledby — no registration handshake needed here.
  useEffect(() => {
    if (shouldAutoFocus && titleRef.current) {
      titleRef.current.focus();
    }
  }, [shouldAutoFocus]);
  return /*#__PURE__*/_jsx(LayoutHeader, {
    ref: ref,
    hasDivider: hasDivider,
    xstyle: xstyle,
    className: className,
    style: style,
    ...rest,
    children: /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 x1cy8zhl x1qughib xjcht0a"
      },
      children: [startContent && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x78zum5 x6s0dn4 x1txdalj x2lah0s"
        },
        children: startContent
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x98rzlu xeuugli xqixskq"
        },
        children: [/*#__PURE__*/_jsx(Heading, {
          ref: titleRef,
          id: titleId,
          level: 2,
          tabIndex: -1,
          xstyle: styles.titleFocusable,
          children: title
        }), subtitle && /*#__PURE__*/_jsx(Text, {
          type: "body",
          size: "sm",
          color: "secondary",
          children: subtitle
        })]
      }), (endContent || onOpenChange) && /*#__PURE__*/_jsxs("div", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 x1txdalj x2lah0s"
          },
          1: {
            className: "x78zum5 x6s0dn4 x1txdalj x2lah0s xhzvc8f x3kzqx6"
          }
        }[!!onOpenChange << 0],
        children: [endContent, onOpenChange && /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          label: t('@astryx.dialog.close'),
          tooltip: t('@astryx.dialog.close'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            color: "inherit"
          }),
          onClick: () => {
            onOpenChange?.(false);
          },
          isIconOnly: true
        })]
      })]
    })
  });
}
DialogHeader.displayName = 'DialogHeader';